/* eslint-disable react/forbid-prop-types */
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import Panel from '@adsk/alloy-react-panel';
import { useLocale } from '@adsk/alloy-react-helpers';
import SelectBox from '@adsk/alloy-select-box';
import FormField from '@adsk/alloy-react-form-field';
import { MemberFilterPanelHeader } from '@common/MemberFilterPanel';
import { FieldWrapper, StyledPanelBody } from './styles';
import { FILTER_KEYS } from './constants';

const ProjectMemberFilterPanel = ({
  open,
  onClose,
  companies,
  isLoadingCompanies,
  roles,
  isLoadingRoles,
  products,
  isLoadingProducts,
  memberStatuses,
  onFilterChange,
  selectedCompany,
  selectedRoles,
  selectedProducts,
  selectedStatuses,
}) => {
  const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));

  const renderHeader = useCallback(
    () => (
      <MemberFilterPanelHeader
        onClose={onClose}
        title={T.translate('PROJECT_MEMBER_FILTER_PANEL_TITLE')}
      />
    ),
    [T, onClose]
  );

  const handleCompanyChange = useCallback(
    (selection) => {
      onFilterChange(FILTER_KEYS.COMPANY_ID, selection?.id);
    },
    [onFilterChange]
  );

  const handleRolesChange = useCallback(
    (selection) => {
      onFilterChange(
        FILTER_KEYS.ROLE_IDS,
        selection?.map(({ id }) => id)
      );
    },
    [onFilterChange]
  );

  const handleProductsChange = useCallback(
    (selection) => {
      onFilterChange(
        FILTER_KEYS.PRODUCTS,
        selection?.map(({ key }) => key)
      );
    },
    [onFilterChange]
  );

  const handleStatusesChange = useCallback(
    (selection) => {
      onFilterChange(
        FILTER_KEYS.STATUS,
        selection?.map(({ id }) => id)
      );
    },
    [onFilterChange]
  );

  return (
    <Panel
      data-testid="MemberFilterPanel"
      open={open}
      renderHeader={renderHeader}
    >
      <StyledPanelBody>
        <div>
          <FieldWrapper>
            <FormField
              label={T.translate('COMPANY_LABEL')}
              labelVariant={FormField.LABEL_VARIANTS.TOP}
            >
              <SelectBox
                data-testid="MemberFilterPanel_Company_SelectBox"
                clearable
                isLoading={isLoadingCompanies}
                ignoreCase
                labelKey="name"
                maxMenuHeight={150}
                onChange={handleCompanyChange}
                options={companies}
                placeholder={T.translate('COMPANY_PLACEHOLDER')}
                searchable
                value={selectedCompany}
                valueKey="id"
              />
            </FormField>
          </FieldWrapper>
          <FieldWrapper>
            <FormField
              label={T.translate('ROLE_LABEL')}
              labelVariant={FormField.LABEL_VARIANTS.TOP}
            >
              <SelectBox
                data-testid="MemberFilterPanel_Role_SelectBox"
                clearable
                multiple
                showValueAsTags
                ignoreCase
                isLoading={isLoadingRoles}
                labelKey="name"
                maxMenuHeight={150}
                onChange={handleRolesChange}
                options={roles}
                placeholder={T.translate('ROLE_PLACEHOLDER')}
                searchable
                value={selectedRoles}
                valueKey="id"
              />
            </FormField>
          </FieldWrapper>
          <FieldWrapper>
            <FormField
              label={T.translate('PRODUCT_ACCESS_LABEL')}
              labelVariant={FormField.LABEL_VARIANTS.TOP}
            >
              <SelectBox
                data-testid="MemberFilterPanel_Product_SelectBox"
                clearable
                multiple
                showValueAsTags
                ignoreCase
                isLoading={isLoadingProducts}
                labelKey="name"
                maxMenuHeight={150}
                onChange={handleProductsChange}
                options={products}
                placeholder={T.translate('PRODUCT_ACCESS_PLACEHOLDER')}
                searchable
                value={selectedProducts}
                valueKey="key"
              />
            </FormField>
          </FieldWrapper>
          <FieldWrapper>
            <FormField
              label={T.translate('STATUS_LABEL')}
              labelVariant={FormField.LABEL_VARIANTS.TOP}
            >
              <SelectBox
                data-testid="MemberFilterPanel_Status_SelectBox"
                clearable
                multiple
                showValueAsTags
                labelKey="name"
                maxMenuHeight={150}
                onChange={handleStatusesChange}
                options={memberStatuses}
                placeholder={T.translate('STATUS_PLACEHOLDER')}
                value={selectedStatuses}
                valueKey="id"
              />
            </FormField>
          </FieldWrapper>
        </div>
      </StyledPanelBody>
    </Panel>
  );
};

ProjectMemberFilterPanel.propTypes = {
  onClose: PropTypes.func,
  open: PropTypes.bool,
  companies: PropTypes.arrayOf(PropTypes.object),
  isLoadingCompanies: PropTypes.bool,
  roles: PropTypes.arrayOf(PropTypes.object),
  isLoadingRoles: PropTypes.bool,
  products: PropTypes.arrayOf(PropTypes.object),
  isLoadingProducts: PropTypes.bool,
  memberStatuses: PropTypes.arrayOf(PropTypes.object),
  onFilterChange: PropTypes.func,
  selectedCompany: PropTypes.object,
  selectedRoles: PropTypes.arrayOf(PropTypes.object),
  selectedProducts: PropTypes.arrayOf(PropTypes.object),
  selectedStatuses: PropTypes.arrayOf(PropTypes.object),
};

ProjectMemberFilterPanel.defaultProps = {
  open: false,
  onClose: () => {},
  companies: [],
  isLoadingCompanies: false,
  roles: [],
  isLoadingRoles: false,
  products: [],
  isLoadingProducts: false,
  memberStatuses: [],
  onFilterChange: () => {},
  selectedCompany: null,
  selectedRoles: [],
  selectedProducts: [],
  selectedStatuses: [],
};

export default ProjectMemberFilterPanel;
